import React, { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  FileDownload,
  AccessTime,
  CheckCircle,
} from '@mui/icons-material';
import SEOHead from '../components/SEO/SEOHead';
import PageHeader from '../components/Layout/PageHeader';
import NewsletterSection from '../components/Layout/NewsletterSection';
import CrossLinks from '../components/Layout/CrossLinks';
import ReferralCTA from '../components/shared/ReferralCTA';

const FISCAL_EVENTS = [
  {
    date: '2026-01-05',
    title: 'GST/HST Credit - Pago trimestral',
    description: 'La CRA deposita tu crédito GST/HST si declaraste impuestos el año anterior.',
    category: 'beneficio',
    who: 'Todos con ingresos bajos/moderados',
  },
  {
    date: '2026-02-28',
    title: 'Fecha límite para recibir tu T4',
    description: 'Tu empleador debe entregarte el T4 (resumen de salario e impuestos) antes de esta fecha.',
    category: 'impuestos',
    who: 'Empleados',
  },
  {
    date: '2026-03-02',
    title: 'Fecha límite de contribución RRSP 2025',
    description: 'Ultimo día para contribuir a tu RRSP y deducirlo en la declaración de impuestos 2025.',
    category: 'ahorro',
    who: 'Todos con espacio RRSP',
  },
  {
    date: '2026-04-05',
    title: 'GST/HST Credit - Pago trimestral',
    description: 'Segundo pago trimestral del crédito GST/HST.',
    category: 'beneficio',
    who: 'Todos con ingresos bajos/moderados',
  },
  {
    date: '2026-04-30',
    title: 'Fecha límite de declaración de impuestos',
    description: 'Ultimo día para presentar tu T1 (declaración federal). Si vives en Quebec, también tu TP-1.',
    category: 'impuestos',
    who: 'Todos los residentes fiscales',
  },
  {
    date: '2026-06-15',
    title: 'Fecha límite para trabajadores independientes',
    description: 'Si eres self-employed, tu declaración vence hoy. Pero si debes impuestos, el pago era el 30 de abril.',
    category: 'impuestos',
    who: 'Self-employed / Freelancers',
  },
  {
    date: '2026-07-05',
    title: 'GST/HST Credit - Pago trimestral',
    description: 'Tercer pago trimestral del crédito GST/HST.',
    category: 'beneficio',
    who: 'Todos con ingresos bajos/moderados',
  },
  {
    date: '2026-07-20',
    title: 'Nuevo año de beneficios - CCB recalculado',
    description: 'El Canada Child Benefit se recalcula basado en tu declaración 2025. Los montos pueden cambiar.',
    category: 'beneficio',
    who: 'Familias con hijos menores de 18',
  },
  {
    date: '2026-09-15',
    title: 'Tercer pago de impuestos trimestrales',
    description: 'Si pagas impuestos por cuotas (instalments), esta es la tercera fecha de pago.',
    category: 'impuestos',
    who: 'Self-employed y quienes pagan por cuotas',
  },
  {
    date: '2026-10-05',
    title: 'GST/HST Credit - Pago trimestral',
    description: 'Cuarto y ultimo pago trimestral del crédito GST/HST del ciclo.',
    category: 'beneficio',
    who: 'Todos con ingresos bajos/moderados',
  },
  {
    date: '2026-12-15',
    title: 'Cuarto pago de impuestos trimestrales',
    description: 'Ultimo pago trimestral de impuestos del año fiscal 2026.',
    category: 'impuestos',
    who: 'Self-employed y quienes pagan por cuotas',
  },
  {
    date: '2026-12-31',
    title: 'Ultima oportunidad - contribuciones TFSA/FHSA 2026',
    description: 'Ultimo día para contribuir a tu TFSA o FHSA y usar el espacio del año 2026.',
    category: 'ahorro',
    who: 'Todos con cuentas registradas',
  },
];

const CATEGORY_CONFIG = {
  impuestos: { color: '#7D5A00', label: 'Impuestos' },
  ahorro: { color: '#1E3A5F', label: 'Ahorro' },
  beneficio: { color: '#1E3A5F', label: 'Beneficio' },
};

function generateICalEvent(event) {
  const dateStr = event.date.replace(/-/g, '');
  return [
    'BEGIN:VEVENT',
    `DTSTART;VALUE=DATE:${dateStr}`,
    `DTEND;VALUE=DATE:${dateStr}`,
    `SUMMARY:${event.title}`,
    `DESCRIPTION:${event.description}`,
    'END:VEVENT',
  ].join('\r\n');
}

function downloadAllIcal() {
  const events = FISCAL_EVENTS.map(generateICalEvent).join('\r\n');
  const ical = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//FinMaple//Calendario Fiscal 2026//ES',
    'CALSCALE:GREGORIAN',
    events,
    'END:VCALENDAR',
  ].join('\r\n');

  const blob = new Blob([ical], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'finmaple-calendario-fiscal-2026.ics';
  a.click();
  URL.revokeObjectURL(url);
}

function getCountdown(dateStr) {
  const now = new Date();
  const target = new Date(dateStr + 'T00:00:00');
  const diff = target - now;
  if (diff <= 0) return null;
  const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
  return days;
}

const schema = {
  '@context': 'https://schema.org',
  '@type': 'Event',
  name: 'Calendario Fiscal Canadá 2026 para Latinos',
  description:
    'Todas las fechas clave de impuestos, beneficios y ahorro en Canadá para 2026. Incluye GST Credit, T4, RRSP, TFSA y más.',
  startDate: '2026-01-01',
  endDate: '2026-12-31',
  location: { '@type': 'Place', name: 'Canadá' },
};

const CalendarioFiscalPage = () => {
  const { lng } = useParams();
  const { i18n } = useTranslation();
  const [filter, setFilter] = useState('todos');

  const events = useMemo(() => {
    if (filter === 'todos') return FISCAL_EVENTS;
    return FISCAL_EVENTS.filter((e) => e.category === filter);
  }, [filter]);

  return (
    <Box sx={{ bgcolor: '#F7F9FC', minHeight: '100vh' }}>
      <SEOHead
        seoKey="taxCalendar"
        title="Calendario fiscal Canadá 2026 - Fechas clave para latinos"
        description="Calendario con todas las fechas fiscales importantes de Canadá 2026: declaración de impuestos, RRSP, TFSA, GST Credit, Child Benefit y más."
        canonical="/calendario-fiscal-2026"
        routeKey="taxCalendar"
        schema={schema}
        noIndex={i18n.language !== 'es'}
      />

      <PageHeader
        variant="functional"
        title="Calendario Fiscal 2026"
        subtitle="Todas las fechas que necesitas saber para tus finanzas en Canadá."
        breadcrumbs={[{ label: 'Calendario fiscal 2026' }]}
      >
        <Box sx={{ mt: 3 }}>
          <Button
            variant="contained"
            startIcon={<FileDownload />}
            onClick={downloadAllIcal}
            sx={{
              bgcolor: '#1E3A5F',
              color: '#fff',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '10px',
              px: 3,
              minHeight: 44,
              '&:hover': { bgcolor: '#1A3152' },
            }}
          >
            Descargar calendario (.ics)
          </Button>
        </Box>
      </PageHeader>

      <Container maxWidth="md" sx={{ py: { xs: 3, md: 5 } }}>

        {/* Filter */}
        <Box sx={{ mb: 4 }}>
          <ToggleButtonGroup
            value={filter}
            exclusive
            onChange={(_, v) => v && setFilter(v)}
            sx={{ flexWrap: 'wrap', gap: 0.5 }}
          >
            <ToggleButton value="todos" sx={{ textTransform: 'none', borderRadius: '8px', minHeight: 44 }}>
              Todos
            </ToggleButton>
            <ToggleButton value="impuestos" sx={{ textTransform: 'none', borderRadius: '8px', minHeight: 44 }}>
              Impuestos
            </ToggleButton>
            <ToggleButton value="ahorro" sx={{ textTransform: 'none', borderRadius: '8px', minHeight: 44 }}>
              Ahorro
            </ToggleButton>
            <ToggleButton value="beneficio" sx={{ textTransform: 'none', borderRadius: '8px', minHeight: 44 }}>
              Beneficios
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Timeline */}
        <Grid container spacing={2}>
          {events.map((event, i) => {
            const daysLeft = getCountdown(event.date);
            const isPast = daysLeft === null;
            const cat = CATEGORY_CONFIG[event.category];
            const dateObj = new Date(event.date + 'T00:00:00');
            const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

            return (
              <Grid item xs={12} key={i}>
                <Card
                  sx={{
                    borderRadius: '10px',
                    borderLeft: `4px solid ${cat.color}`,
                    opacity: isPast ? 0.6 : 1,
                    boxShadow: isPast
                      ? '0 1px 4px rgba(0,0,0,0.04)'
                      : '0 2px 12px rgba(0,0,0,0.08)',
                  }}
                >
                  <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    {/* Date badge */}
                    <Box
                      sx={{
                        minWidth: 56,
                        textAlign: 'center',
                        bgcolor: `${cat.color}12`,
                        borderRadius: '10px',
                        py: 1,
                        px: 0.5,
                      }}
                    >
                      <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: cat.color, textTransform: 'uppercase' }}>
                        {monthNames[dateObj.getMonth()]}
                      </Typography>
                      <Typography sx={{ fontSize: '1.5rem', fontWeight: 600, color: '#1A1A1A', lineHeight: 1 }}>
                        {dateObj.getDate()}
                      </Typography>
                    </Box>

                    {/* Content */}
                    <Box sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1A1A1A' }}>
                          {event.title}
                        </Typography>
                        <Chip
                          label={cat.label}
                          size="small"
                          sx={{
                            bgcolor: `${cat.color}15`,
                            color: cat.color,
                            fontWeight: 600,
                            fontSize: '0.875rem',
                          }}
                        />
                      </Box>
                      <Typography variant="body2" sx={{ color: '#1A1A1A', lineHeight: 1.6, mb: 1 }}>
                        {event.description}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#5F6B7A' }}>
                        Aplica a: {event.who}
                      </Typography>
                    </Box>

                    {/* Countdown */}
                    <Box sx={{ minWidth: 80, textAlign: 'center', pt: 0.5 }}>
                      {isPast ? (
                        <CheckCircle sx={{ color: '#5F6B7A', fontSize: 28 }} />
                      ) : (
                        <>
                          <AccessTime sx={{ color: daysLeft <= 30 ? '#D32F2F' : '#1E3A5F', fontSize: 20 }} />
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: '1.25rem',
                              color: daysLeft <= 30 ? '#D32F2F' : '#1E3A5F',
                              lineHeight: 1,
                            }}
                          >
                            {daysLeft}
                          </Typography>
                          <Typography sx={{ fontSize: '0.875rem', color: '#5F6B7A' }}>
                            días
                          </Typography>
                        </>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Container>

      {/* Wealthsimple CTA */}
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ bgcolor: '#F7F9FC', borderRadius: '12px', p: { xs: 2.5, md: 3 }, border: '1px solid #EEF0F3', textAlign: 'center' }}>
          <Typography sx={{ fontWeight: 600, fontSize: '15px', color: '#1A1A1A', mb: 0.5 }}>
            {lng === 'fr' ? 'Maximisez vos économies d\'impôt' : lng === 'en' ? 'Maximize your tax savings' : 'Maximiza tu ahorro fiscal'}
          </Typography>
          <Typography sx={{ fontSize: '14px', color: '#5F6B7A', lineHeight: 1.7, mb: 2 }}>
            {lng === 'fr'
              ? 'Contribuez à votre REER ou CÉLI avant les dates limites. Wealthsimple — sans frais cachés.'
              : lng === 'en'
              ? 'Contribute to your RRSP or TFSA before the deadlines. Wealthsimple — no hidden fees.'
              : 'Contribuye a tu RRSP o TFSA antes de las fechas límite. Wealthsimple — sin comisiones ocultas.'}
          </Typography>
          <ReferralCTA bank="wealthsimple">
            {lng === 'fr' ? 'Ouvrir un compte Wealthsimple' : lng === 'en' ? 'Open a Wealthsimple account' : 'Abrir cuenta Wealthsimple'}
          </ReferralCTA>
        </Box>
      </Container>

      <Container maxWidth="md" sx={{ pb: 4 }}>
        <CrossLinks
          title={lng === 'fr' ? 'Outils connexes' : lng === 'en' ? 'Related tools' : 'Herramientas relacionadas'}
          links={[
            { label: lng === 'fr' ? 'Calculatrice de salaire net' : lng === 'en' ? 'Net salary calculator' : 'Calculadora salario neto', to: '/herramientas/calculadora-salario-neto' },
            { label: lng === 'fr' ? 'Calculatrice REER vs CÉLI' : lng === 'en' ? 'RRSP vs TFSA calculator' : 'Calculadora RRSP vs TFSA', to: '/herramientas/calculadora-rrsp-vs-tfsa' },
            { label: lng === 'fr' ? 'Guide impôts Québec 2026' : lng === 'en' ? 'Quebec tax guide 2026' : 'Guía impuestos Quebec 2026', to: '/blog/guia-impuestos-latinos-quebec-2026' },
            { label: 'T4 explicado', to: '/blog/t4-explicado-espanol-impuestos-canada' },
          ]}
        />
      </Container>

      <NewsletterSection />
    </Box>
  );
};

export default CalendarioFiscalPage;
